import { colorTokens, colors as colors$1, sizes as sizes$1, typography as typography$1, shadows, motion } from '@hover/blueprint-theme';
import _mergeDeepRight from 'ramda/src/mergeDeepRight';
import * as tokens from '@hover/tokens-typography';
import _mapObjIndexed from 'ramda/src/mapObjIndexed';
import { position, transitions } from 'polished';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
} // For backward compatibility with legacy Blueprint colors
// (e.g: `primary500` instead of Chakra-style `primary.500`)


var legacyColors = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, colorTokens), {}, {
  neutral000: colorTokens.neutral0
});

var palettes = {
  primary: colors$1.primary,
  secondary: /*#__PURE__*/_objectSpread2({
    50: colors$1.secondary[100]
  }, colors$1.secondary),
  success: colors$1.success,
  warning: colors$1.warning,
  danger: colors$1.danger,
  neutral: /*#__PURE__*/_objectSpread2({
    800: colors$1.neutral[700]
  }, colors$1.neutral),
  brandNavy: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandNavy[100]
  }, colors$1.brandNavy),
  brandGreen: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandGreen[100]
  }, colors$1.brandGreen),
  brandBrown: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandBrown[100]
  }, colors$1.brandBrown),
  brandRed: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandRed[100]
  }, colors$1.brandRed),
  brandTan: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandTan[100]
  }, colors$1.brandTan),
  brandOrange: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandOrange[100]
  }, colors$1.brandOrange),
  brandYellow: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandYellow[100]
  }, colors$1.brandYellow),
  brandPurple: /*#__PURE__*/_objectSpread2({
    50: colors$1.brandPurple[100]
  }, colors$1.brandPurple),
  darkAlpha: colors$1.darkAlpha,
  lightAlpha: colors$1.lightAlpha
};

var colors = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, palettes), {}, {
  blue: palettes.primary,
  green: palettes.brandGreen,
  orange: palettes.brandOrange,
  purple: palettes.brandPurple,
  red: palettes.brandRed,
  yellow: palettes.brandYellow
}, legacyColors); // dimensions, however the default theme defines size presets for `1` and `0.5`
// which overrides this behavior for those two specific values. Here we're
// re-defining `1` and `0.5` to ensure consistent fraction ↔ % behavior
//
// Documentation: https://chakra-ui.com/docs/features/style-props#layout-width-and-height
// Implementation: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/styled-system/src/utils/index.ts#L38
//
// Offending space presets: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/theme/src/foundations/spacing.ts#L3-L4
// Which get folded into `sizes`: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/theme/src/foundations/sizes.ts#L31


var sizes = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, sizes$1), {}, {
  1: '100%',
  0.5: '50%'
});

var typography = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, typography$1), {}, {
  fonts: {
    heading: tokens.familyBrand,
    body: tokens.familyBody,
    mono: tokens.familyCode,
    display: tokens.familyDisplay
  }
});

var legacyTypography = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/_mergeDeepRight(typography$1, typography$1.legacy)), {}, {
  fonts: {
    heading: tokens.legacyFamilyBrand,
    body: tokens.legacyFamilyBody,
    mono: tokens.familyCode,
    display: tokens.legacyFamilyDisplay
  }
});

var focusSelector = ':focus';
var focusVisibleSelector = ':focus-visible';
var focusVisibleInvertSelector = ':focus:not(:focus-visible)';

var invert = function invert(styles) {
  return _mapObjIndexed(function () {
    return 'none';
  }, styles);
};
/**
 * Override browser default focus styles with provided styles.
 *
 * Currently supports `:focus-visible` via the second parameter. Because not
 * all browsers (looking at you Safari) support `:focus-visible`, we need to
 * first apply any provides styles to `:focus` and then remove them in browsers
 * that support `:focus-visible`. Passing a `true` to `visibleOnly` will simply
 * try to "invert" the provided focus styles by setting each property to
 * `none`. For finer grained control, pass a `CSSObject` as the `visibleOnly`
 * parameter to provide the default unfocused state that will be applied to
 * browsers that do support `:focus-visible`.
 *
 * @example
 * Apply custom styles to `:focus`
 * ```ts
 * resetFocus({ border: '1px solid red' })
 * ```
 *
 * @example
 * Apply custom styles to `:focus-visible` (this will only work if the default state
 * does not include a `border`, see next example for how to handle that case)
 * ```ts
 * resetFocus({ border: '1px solid red' }, true);
 * ```
 *
 * @example
 * Apply custom styles to `:focus-visible` with unfocused defaults
 * ```ts
 * resetFocus({ border: '1px solid red' }, { border: '1px solid gray' }});
 * ```
 *
 * @param styles styles to apply for `:focus` state
 * @param visibleOnly whether to apply styles only to the `:focus-visible` state (see above)
 * @returns style object with custom focus styels and browser focus reset
 */


var resetFocus = function resetFocus() {
  var _ref;

  var styles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var visibleOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return _ref = {}, _defineProperty(_ref, focusSelector, _objectSpread2({
    outline: 0
  }, styles)), _defineProperty(_ref, focusVisibleSelector, _objectSpread2({
    outline: 0
  }, styles)), _defineProperty(_ref, focusVisibleInvertSelector, _objectSpread2({}, visibleOnly ? visibleOnly === true ? invert(styles) : visibleOnly : {})), _defineProperty(_ref, '::-moz-focus-inner', {
    border: 0,
    outline: 0
  }), _ref;
};

var noFocus = /*#__PURE__*/resetFocus();

var subtleFocus = /*#__PURE__*/_objectSpread2({
  position: 'relative',
  ':before': /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, /*#__PURE__*/position('absolute', '-0.15em', '-0.2em')), /*#__PURE__*/transitions(['box-shadow'], motion.easings[250])), {}, {
    borderRadius: '200',
    boxShadow: shadows.invisibleNeutral,
    content: "''",
    opacity: 0.5
  })
}, /*#__PURE__*/resetFocus({
  position: 'relative',
  ':before': {
    boxShadow: shadows.haloNeutral
  }
}));

export { _objectSpread2 as _, _defineProperty as a, legacyTypography as b, colors as c, subtleFocus as d, legacyColors as l, noFocus as n, resetFocus as r, sizes as s, typography as t };